(() => {
  'use strict';

  const initialized = new WeakSet();

  // SEARCH MASTHEAD COPY TEMPLATES
  // Edit, remove, or reorder these lines to change the rotating customer-facing copy.
  const SEARCH_COPY_TEMPLATES = [
    'Explore {count} authentic {queryLabel} matched to your search.',
    'See {count} vintage {queryLabel} from the current VCC archive.',
    'Browse {count} genuine {queryLabel} selected for this search.',
    'Shop {count} rare {queryLabel} from the vintage archive.',
    'Found {count} one-of-one {queryLabel} selected for your search.',
    'Explore {count} verified vintage {queryLabel}.',
    'See {count} high-quality {queryLabel} from the archive.',
    'Browse {count} authentic vintage {queryLabel}.',
    'Found {count} rare {queryLabel} matched to your search.',
    'Start with {count} {queryLabel} chosen for this search.',
    'See {count} genuine vintage {queryLabel}.',
    'Explore {count} archive-verified {queryLabel}.',
    'Browse {count} one-of-one {queryLabel} from the archive.',
    'Found {count} authentic {queryLabel}.',
    'Shop {count} rare vintage {queryLabel}.',
    'See {count} {queryLabel} matched closely to your search.',
    'Explore {count} high-quality vintage {queryLabel}.',
    'Browse {count} genuine archive {queryLabel}.',
    'Found {count} vintage {queryLabel} in the current archive.',
    'Start with {count} authentic {queryLabel} from VCC.',
  ];

  const SEARCH_RELATED_LINES = [
    'Related archive finds continue below.',
    'More similar vintage pieces follow.',
    'The wider archive continues with related finds.',
    'Similar VCC pieces follow the closest matches.',
    'Keep browsing for related one-of-one pieces.',
  ];

  const SEARCH_ZERO_MATCH_TEMPLATES = [
    'No close {queryLabel} match the current filters. Related archive pieces follow.',
    'No close {queryLabel} remain under these filters. Similar vintage finds follow.',
    'These filters leave no close {queryLabel}. Related one-of-one pieces follow.',
  ];

  const SEARCH_TYPE_LABELS = {
    't-shirt': ['T-shirt', 'T-shirts'],
    't-shirts': ['T-shirt', 'T-shirts'],
    tee: ['T-shirt', 'T-shirts'],
    tees: ['T-shirt', 'T-shirts'],
    hat: ['hat', 'hats'],
    hats: ['hat', 'hats'],
    jacket: ['jacket', 'jackets'],
    jackets: ['jacket', 'jackets'],
    jersey: ['jersey', 'jerseys'],
    jerseys: ['jersey', 'jerseys'],
    pants: ['pair of pants', 'pairs of pants'],
    shorts: ['pair of shorts', 'pairs of shorts'],
    skirt: ['skirt', 'skirts'],
    skirts: ['skirt', 'skirts'],
    sleeveless: ['sleeveless top', 'sleeveless tops'],
    sweatshirt: ['sweatshirt', 'sweatshirts'],
    sweatshirts: ['sweatshirt', 'sweatshirts'],
    tracksuit: ['tracksuit', 'tracksuits'],
    tracksuits: ['tracksuit', 'tracksuits'],
    dress: ['dress', 'dresses'],
    dresses: ['dress', 'dresses'],
  };

  const cleanText = (value) => String(value || '').replace(/\s+/g, ' ').trim();

  const stableIndex = (query, length, salt = '') => {
    const seed = `${cleanText(query).toLowerCase()}|${new Date().toISOString().slice(0, 10)}|${salt}`;
    let hash = 2166136261;
    for (let index = 0; index < seed.length; index += 1) {
      hash ^= seed.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return Math.abs(hash) % Math.max(1, length);
  };

  const smartTitleCase = (value) => {
    const acronyms = new Set(['mlb', 'nba', 'nfl', 'nhl', 'ncaa', 'usa', 'wwe']);
    return cleanText(value)
      .toLowerCase()
      .replace(/\b[a-z0-9][a-z0-9']*\b/g, (word) => {
        if (acronyms.has(word)) return word.toUpperCase();
        return word.charAt(0).toUpperCase() + word.slice(1);
      });
  };

  const typeLabel = (type, count) => {
    const key = cleanText(type?.label || type?.name).toLowerCase();
    const labels = SEARCH_TYPE_LABELS[key];
    if (labels) return count === 1 ? labels[0] : labels[1];
    if (!key) return count === 1 ? 'piece' : 'pieces';
    return count === 1 ? key : `${key.replace(/s$/, '')}s`;
  };

  const naturalList = (values) => {
    const items = Array.from(new Set(values.filter(Boolean)));
    if (items.length < 2) return items[0] || '';
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
  };

  const buildQueryLabel = (analysis, rawQuery, count) => {
    const subject = analysis?.subject;
    const types = analysis?.types || [];
    const type = types[0];
    const typeText = naturalList(types.map((item) => typeLabel(item, types.length > 1 ? 2 : count)));
    const color = cleanText(analysis?.colors?.[0]?.label || analysis?.colors?.[0]?.name).toLowerCase();
    const size = cleanText(analysis?.sizes?.[0]?.label || analysis?.sizes?.[0]?.name);
    const subjectType = cleanText(subject?.type).toLowerCase();
    let subjectLabel = cleanText(subject?.label || subject?.name);
    const genericCatalogTag = /catalog_tag/i.test(subjectType) && !subjectLabel.includes(' ');

    if (subjectLabel && (/(animal|wildlife|nature)/i.test(subjectType) || genericCatalogTag)) {
      subjectLabel = subjectLabel.toLowerCase();
    }

    if (subjectLabel && type) {
      return cleanText([color, subjectLabel, size, typeText].filter(Boolean).join(' '));
    }

    if (subjectLabel) {
      if (/(animal|wildlife|nature)/i.test(subjectType)) {
        return `${subjectLabel}-themed ${count === 1 ? 'piece' : 'pieces'}`;
      }
      if (/catalog_collection/i.test(subjectType)) {
        return `${subjectLabel} ${count === 1 ? 'find' : 'finds'}`;
      }
      return `${subjectLabel} ${count === 1 ? 'piece' : 'pieces'}`;
    }

    if (type) {
      return cleanText([color, size, typeText].filter(Boolean).join(' '));
    }

    const rawLabel = smartTitleCase(rawQuery);
    return rawLabel ? `${rawLabel} ${count === 1 ? 'piece' : 'pieces'}` : '';
  };

  const updateSearchCopy = (root) => {
    const copy = root.querySelector('[data-vcc-search-masthead-copy]');
    if (!copy) return;
    if (root.dataset.searchCuration === 'fallback-locked') {
      copy.dataset.vccSearchCopyLocked = 'true';
      return;
    }

    const query = cleanText(copy.dataset.searchQuery);
    const broadCount = Number(copy.dataset.broadCount) || 0;
    if (!query || !broadCount) return;
    let latestResult = null;
    let manifestCloseCount = root.dataset.searchMappedCloseCount === undefined
      ? null
      : Math.max(0, Number(root.dataset.searchMappedCloseCount) || 0);

    const renderCopy = (result, resolvedCount, basis, resolvedTotal) => {
      if (
        root.dataset.searchCuration === 'fallback-locked'
        || copy.dataset.vccSearchCopyLocked === 'true'
      ) return;
      const suppliedCount = Number(resolvedCount);
      const closeCount = Number.isFinite(suppliedCount)
        ? Math.max(0, suppliedCount)
        : Math.max(1, Number(result?.count) || 1);
      const queryLabel = buildQueryLabel(result.analysis, query, closeCount);
      if (!queryLabel) return;
      const suppliedTotal = Number(resolvedTotal);
      const totalCount = Number.isFinite(suppliedTotal)
        ? Math.max(closeCount, suppliedTotal)
        : broadCount;
      const relatedCount = Math.max(0, totalCount - closeCount);
      if (closeCount === 0) {
        const zeroTemplate = SEARCH_ZERO_MATCH_TEMPLATES[
          stableIndex(query, SEARCH_ZERO_MATCH_TEMPLATES.length, 'zero')
        ];
        copy.textContent = zeroTemplate.replaceAll('{queryLabel}', queryLabel);
        copy.dataset.closeMatchCount = '0';
        copy.dataset.closeMatchBasis = basis || result.basis || '';
        copy.dataset.relatedMatchCount = String(relatedCount);
        return;
      }

      const template = SEARCH_COPY_TEMPLATES[stableIndex(query, SEARCH_COPY_TEMPLATES.length, 'lead')];
      const relatedLine = relatedCount > 0
        ? ` ${SEARCH_RELATED_LINES[stableIndex(query, SEARCH_RELATED_LINES.length, 'related')]}`
        : '';
      const count = new Intl.NumberFormat('en-US').format(closeCount);
      if (basis === 'mapped-manifest' && totalCount > closeCount) {
        const total = new Intl.NumberFormat('en-US').format(totalCount);
        copy.textContent = `${count} close ${queryLabel} ${closeCount === 1 ? 'appears' : 'appear'} first. Browse ${total} curated results including related vintage pieces.`;
      } else {
        copy.textContent = `${template.replaceAll('{count}', count).replaceAll('{queryLabel}', queryLabel)}${relatedLine}`;
      }
      copy.dataset.closeMatchCount = String(closeCount);
      copy.dataset.closeMatchBasis = basis || result.basis || '';
      copy.dataset.relatedMatchCount = String(relatedCount);
    };

    root.addEventListener('vcc:search-manifest-ready', (event) => {
      if (root.dataset.searchCuration === 'fallback-locked') return;
      if (cleanText(event.detail?.query).toLowerCase() !== query.toLowerCase()) return;
      manifestCloseCount = Math.max(0, Number(event.detail?.closeCount) || 0);
      if (!latestResult && event.detail?.analysis) {
        latestResult = {
          analysis: event.detail.analysis,
          count: manifestCloseCount,
          basis: 'mapped-manifest'
        };
      }
      if (latestResult) {
        renderCopy(
          latestResult,
          manifestCloseCount,
          'mapped-manifest',
          Number(event.detail?.resultCount)
        );
      }
      copy.dataset.vccSearchCopyLocked = 'true';
    });

    root.addEventListener('vcc:search-curation-locked', (event) => {
      if (event.detail?.mode === 'fallback') {
        copy.dataset.vccSearchCopyLocked = 'true';
      }
    });

    const applyCopy = () => {
      if (
        root.dataset.searchCuration === 'fallback-locked'
        || copy.dataset.vccSearchCopyLocked === 'true'
      ) return true;
      const engine = window.VCCSmartSearch || window.VCCSearchIntelligence;
      if (!engine?.estimateCloseMatchesAsync) return false;

      engine
        .estimateCloseMatchesAsync(query, broadCount)
        .then((result) => {
          if (
            !copy.isConnected
            || root.dataset.searchCuration === 'fallback-locked'
            || copy.dataset.vccSearchCopyLocked === 'true'
            || !result
            || !Number.isFinite(Number(result.count))
          ) return;
          latestResult = result;
          renderCopy(
            result,
            manifestCloseCount === null ? result.count : manifestCloseCount,
            manifestCloseCount === null ? result.basis : 'mapped-manifest'
          );
        })
        .catch(() => {});

      return true;
    };

    if (applyCopy()) return;
    document.addEventListener('vcc:search-intelligence-ready', applyCopy, { once: true });
    window.setTimeout(applyCopy, 1200);
  };

  const offsetBottomWithin = (element, ancestor) => {
    let node = element;
    let offset = 0;

    while (node && node !== ancestor) {
      offset += node.offsetTop || 0;
      node = node.offsetParent;
    }

    return offset + element.offsetHeight;
  };

  const offsetTopWithin = (element, ancestor) => {
    let node = element;
    let offset = 0;

    while (node && node !== ancestor) {
      offset += node.offsetTop || 0;
      node = node.offsetParent;
    }

    return offset;
  };

  const initRoot = (root) => {
    if (!root || initialized.has(root)) return;

    const masthead = root.querySelector('[data-vcc-ac-masthead]');
    const backdrop = root.querySelector('[data-vcc-ac-masthead-backdrop]');
    const toolbar = root.querySelector('[data-ac-toolbar]');

    if (!masthead || !backdrop || !toolbar) return;

    initialized.add(root);
    updateSearchCopy(root);

    let animationFrame = 0;

    const measure = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        const activeFilters = root.querySelector('.vcc-ac-active:not([hidden])');
        const filterStart = activeFilters && activeFilters.offsetHeight > 0 ? activeFilters : toolbar;
        const stopsBeforeFilters = root.dataset.vccAcMastheadEnd === 'before-filters';
        const measuredOffset = stopsBeforeFilters
          ? offsetTopWithin(filterStart, root)
          : offsetBottomWithin(toolbar, root);
        const height = Math.max(0, Math.ceil(measuredOffset));
        if (height > 0) root.style.setProperty('--vcc-ac-masthead-height', `${height}px`);
      });
    };

    measure();
    window.addEventListener('resize', measure, { passive: true });

    let resizeObserver = null;
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(masthead);
      resizeObserver.observe(toolbar);

      const activeFilters = root.querySelector('.vcc-ac-active');
      if (activeFilters) resizeObserver.observe(activeFilters);
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure).catch(() => {});
    }

    root.addEventListener(
      'vcc-ac-masthead:destroy',
      () => {
        window.removeEventListener('resize', measure);
        resizeObserver?.disconnect();
        window.cancelAnimationFrame(animationFrame);
      },
      { once: true }
    );
  };

  const initAll = (scope = document) => {
    scope
      .querySelectorAll?.('[data-vcc-ac][data-vcc-ac-masthead-enabled="true"]')
      .forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-vcc-ac][data-vcc-ac-masthead-enabled="true"]').forEach((root) => {
      root.dispatchEvent(new CustomEvent('vcc-ac-masthead:destroy'));
    });
  });
})();
